import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDownLeft,
  ArrowUpRight,
  ChevronRight,
  Heart,
  ImageOff,
  LifeBuoy,
  LocateFixed,
  MapPin,
  Package,
  Pencil,
  Receipt,
  ShoppingBag,
  Star,
  Trash2,
  User,
  Wallet,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AccountSidebar from '../components/AccountSidebar';
import LocationPickerMap from '../components/LocationPickerMap';
import { SkeletonBlock } from '../components/Skeleton';
import usePageMetadata from '../hooks/usePageMetadata';
import useCurrentLocation from '../hooks/useCurrentLocation';
import { useAuth } from '../context/AuthContext';
import { useFavourites } from '../context/FavouritesContext';
import { useOptionalDeliveryLocation } from '../context/DeliveryLocationContext';
import { isStaleLocationError } from '../utils/staleLocation';
import { useToast } from '../context/ToastContext';
import * as authApi from '../api/endpoints/auth';
import * as addressesApi from '../api/endpoints/addresses';
import * as walletApi from '../api/endpoints/wallet';
import * as favouritesApi from '../api/endpoints/favourites';
import { btnMd, btnPrimary, focusRing, homeShell, inputClass, labelClass } from '../styles/ui';

// Quick actions — every one is an existing route.
const QUICK_ACTIONS = [
  { id: 'orders', to: '/orders', icon: Package, title: 'My Orders', description: 'Track and manage your orders' },
  { id: 'favourites', to: '/favourites', icon: Heart, title: 'Favourites', description: 'View your saved products' },
  { id: 'shop', to: '/categories', icon: ShoppingBag, title: 'Shop Groceries', description: 'Browse fresh products' },
  { id: 'support', to: '/support', icon: LifeBuoy, title: 'Help & Support', description: 'Get help or contact us' },
];

const money = (value) => `₹${Number(value || 0).toFixed(2)}`;

const formatTxnDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}, ${date.toLocaleTimeString(
    [],
    { hour: '2-digit', minute: '2-digit' }
  )}`;
};

const ProfilePage = () => {
  const { customer, setCustomer } = useAuth();
  const [profileForm, setProfileForm] = useState({ name: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);

  const [addresses, setAddresses] = useState(null);
  const [newAddress, setNewAddress] = useState({ label: '', line1: '', line2: '', city: '', state: '', pincode: '', landmark: '', coordinates: null });
  const [addingAddress, setAddingAddress] = useState(false);
  const [addressError, setAddressError] = useState(null);
  const [addressFormOpen, setAddressFormOpen] = useState(false);
  const { locating, locationError, captureLocation } = useCurrentLocation();

  const [wallet, setWallet] = useState({ status: 'loading', data: null, error: null });

  const [favourites, setFavourites] = useState({ status: 'loading', products: [], error: null });
  const { removeFavourite } = useFavourites();
  const { showToast } = useToast();

  usePageMetadata({
    title: 'My Account | Fresh & Homely',
    description: 'Manage your Fresh & Homely profile and delivery addresses.',
    canonicalPath: '/account',
  });

  useEffect(() => {
    if (customer) setProfileForm({ name: customer.name || '' });
  }, [customer]);

  const loadAddresses = () => {
    addressesApi.listAddresses().then((res) => setAddresses(res.data || []));
  };

  useEffect(loadAddresses, []);

  const loadWallet = () => {
    setWallet({ status: 'loading', data: null, error: null });
    walletApi
      .getWallet({ limit: 5 })
      .then((res) => setWallet({ status: 'success', data: res.data, error: null }))
      .catch((err) => setWallet({ status: 'error', data: null, error: err.message }));
  };

  useEffect(loadWallet, []);

  // The preview's products are projected for the delivery location, so it is
  // reloaded when that changes; only the latest request may write it.
  const locationVersion = useOptionalDeliveryLocation()?.locationVersion ?? 0;
  const favouritesRequestRef = useRef(0);
  const loadFavourites = () => {
    const requestId = ++favouritesRequestRef.current;
    setFavourites({ status: 'loading', products: [], error: null });
    favouritesApi
      .listFavourites()
      .then((res) => {
        if (requestId === favouritesRequestRef.current) {
          setFavourites({ status: 'success', products: res.data?.products || [], error: null });
        }
      })
      .catch((err) => {
        if (requestId !== favouritesRequestRef.current || isStaleLocationError(err)) return;
        setFavourites({ status: 'error', products: [], error: err.message });
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(loadFavourites, [locationVersion]);

  const handleRemoveFavourite = async (productId) => {
    try {
      const result = await removeFavourite(productId);
      if (result?.status !== 'updated') return;
      setFavourites((prev) => ({ ...prev, products: prev.products.filter((p) => p._id !== productId) }));
      showToast('Removed from favourites', { tone: 'info' });
    } catch {
      // FavouritesContext already rolled back its own state; leave this list as-is.
    }
  };

  const handleProfileSubmit = async (event) => {
    event.preventDefault();
    setProfileSaving(true);
    setProfileMessage(null);
    try {
      const response = await authApi.patchMe(profileForm);
      setCustomer(response.data);
      setProfileMessage('Profile updated');
      setEditingProfile(false);
    } catch (err) {
      setProfileMessage(err.message);
    } finally {
      setProfileSaving(false);
    }
  };

  const handleAddAddress = async (event) => {
    event.preventDefault();
    setAddressError(null);
    if (!newAddress.coordinates) {
      setAddressError('Please pin your location before saving this address.');
      return;
    }
    setAddingAddress(true);
    try {
      await addressesApi.createAddress(newAddress);
      setNewAddress({ label: '', line1: '', line2: '', city: '', state: '', pincode: '', landmark: '', coordinates: null });
      loadAddresses();
      setAddressFormOpen(false);
      showToast('Address added');
    } catch (err) {
      setAddressError(err.message);
    } finally {
      setAddingAddress(false);
    }
  };

  const handlePinLocation = async () => {
    const coordinates = await captureLocation();
    if (coordinates) setNewAddress((p) => ({ ...p, coordinates }));
  };

  const handleMapAddressChange = useCallback((geoAddress) => {
    setNewAddress((prev) => ({
      ...prev,
      line1: geoAddress.line1 || prev.line1 || '',
      line2: geoAddress.line2 || prev.line2 || '',
      city: geoAddress.city || prev.city || '',
      state: geoAddress.state || prev.state || '',
      pincode: geoAddress.pincode || prev.pincode || '',
      landmark: geoAddress.landmark || prev.landmark || '',
    }));
  }, []);

  const handleSetDefault = async (id) => {
    await addressesApi.setDefaultAddress(id);
    loadAddresses();
    showToast('Default address updated');
  };

  const handleDelete = async (id) => {
    await addressesApi.deleteAddress(id);
    loadAddresses();
    showToast('Address removed', { tone: 'info' });
  };

  if (!customer) {
    return (
      <>
        <Navbar />
        <main className="pt-28 pb-20">
          <div className={homeShell}>
            <SkeletonBlock className="h-64 rounded-3xl" />
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const transactions = wallet.data?.transactions || [];

  return (
    <>
      <Navbar />

      <main className="relative overflow-hidden pt-24 sm:pt-28 pb-12 sm:pb-16 bg-gradient-to-b from-emerald-50/60 via-white to-emerald-50/40">
        {/* Decorative only: an owned generated asset, never over a control. */}
        <div className="pointer-events-none select-none absolute inset-0 overflow-hidden" aria-hidden="true">
          <span className="absolute -left-24 bottom-24 w-80 h-80 rounded-full bg-emerald-200/25 blur-3xl" />
          <img
            src="/support-hero.png"
            alt=""
            className="hidden xl:block absolute right-[2%] 2xl:right-[4%] -top-6 w-[20rem] 2xl:w-[24rem] object-contain opacity-80"
          />
        </div>

        <div className={`${homeShell} relative`}>
          <div className="grid grid-cols-1 lg:grid-cols-[18rem_minmax(0,1fr)] xl:grid-cols-[19rem_minmax(0,1fr)_23rem] gap-5 xl:gap-7 items-start">
            <div className="lg:sticky lg:top-28">
              <AccountSidebar />
            </div>

            {/* ── Main column ───────────────────────────────────────────── */}
            <div className="min-w-0 space-y-5">
              <div>
                <h1 className="serif text-3xl sm:text-4xl text-emerald-950 leading-tight">My Account</h1>
                <p className="text-sm text-ink-secondary mt-1.5">
                  Manage your profile, addresses, orders and more — all in one place.
                </p>
              </div>

              {/* Wallet — every figure comes from GET /wallet. */}
              <section
                id="wallet"
                aria-label="Wallet"
                className="relative overflow-hidden rounded-[1.5rem] bg-gradient-primary text-white shadow-lifted p-6 sm:p-7 scroll-mt-28"
              >
                <span className="pointer-events-none absolute -right-10 -bottom-12 w-48 h-48 rounded-full bg-white/10" aria-hidden="true" />
                <span className="pointer-events-none absolute right-16 -top-16 w-40 h-40 rounded-full bg-white/10" aria-hidden="true" />
                <div className="relative flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                  <span className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center shrink-0">
                    <Wallet size={28} aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[15px] font-medium text-white/90">Wallet Balance</p>
                    {wallet.status === 'success' && (
                      <p className="text-4xl sm:text-5xl font-bold leading-[1.1] mt-0.5">
                        {money(wallet.data.balance)}
                      </p>
                    )}
                    {wallet.status === 'loading' && (
                      <p className="text-sm text-white/80 mt-2">Loading wallet…</p>
                    )}
                    {wallet.status === 'error' && (
                      <div className="mt-1">
                        <p className="text-sm text-white/90">Unable to load balance</p>
                        <button
                          type="button"
                          onClick={loadWallet}
                          className={`text-sm font-medium underline underline-offset-4 ${focusRing}`}
                        >
                          Try again
                        </button>
                      </div>
                    )}
                    {wallet.status === 'success' && (
                      <div className="flex items-center gap-8 mt-3 text-sm sm:hidden">
                        <span>
                          <span className="block text-white/80">Credited</span>
                          <span className="font-semibold">{money(wallet.data.totalCredited)}</span>
                        </span>
                        <span>
                          <span className="block text-white/80">Debited</span>
                          <span className="font-semibold">{money(wallet.data.totalDebited)}</span>
                        </span>
                      </div>
                    )}
                    {/* No "Add Money": the customer API exposes only GET /wallet
                        and GET /wallet/history — there is no top-up endpoint, so
                        a button here would be dead. */}
                  </div>

                  {wallet.status === 'success' && (
                    <div className="hidden sm:flex items-stretch gap-3 shrink-0">
                      <span className="rounded-2xl bg-white/15 px-7 py-4 text-center">
                        <span className="block text-xs text-white/85">Credited</span>
                        <span className="block text-xl font-bold mt-0.5">{money(wallet.data.totalCredited)}</span>
                      </span>
                      <span className="rounded-2xl bg-white/15 px-7 py-4 text-center">
                        <span className="block text-xs text-white/85">Debited</span>
                        <span className="block text-xl font-bold mt-0.5">{money(wallet.data.totalDebited)}</span>
                      </span>
                    </div>
                  )}
                </div>
              </section>

              {/* Favourites preview */}
              <section aria-label="Favourites" className="bg-white rounded-[1.5rem] border border-emerald-100 shadow-soft p-6">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <h2 className="flex items-center gap-2.5 text-xl font-bold text-emerald-950">
                    <Heart size={20} className="text-accent-500" aria-hidden="true" />
                    Favourites
                  </h2>
                  {favourites.status === 'success' && favourites.products.length > 0 && (
                    <Link
                      to="/favourites"
                      className={`text-sm font-medium text-emerald-700 hover:text-emerald-800 ${focusRing}`}
                    >
                      View all ({favourites.products.length})
                    </Link>
                  )}
                </div>

                {favourites.status === 'loading' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                    {Array.from({ length: 3 }).map((_, index) => (
                      <SkeletonBlock key={index} className="h-16 rounded-xl" />
                    ))}
                  </div>
                )}

                {favourites.status === 'error' && (
                  <div>
                    <p className="text-sm text-red-600 mb-2">{favourites.error}</p>
                    <button type="button" onClick={loadFavourites} className={`text-sm font-semibold text-emerald-700 ${focusRing}`}>
                      Try again
                    </button>
                  </div>
                )}

                {favourites.status === 'success' && favourites.products.length === 0 && (
                  <p className="text-sm text-ink-secondary">
                    You haven't saved any favourites yet. Tap the heart icon on any product to save it here.
                  </p>
                )}

                {favourites.status === 'success' && favourites.products.length > 0 && (
                  <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                    {favourites.products.slice(0, 3).map((product) => (
                      <li
                        key={product._id}
                        className="flex items-center gap-3 rounded-2xl border border-emerald-100 bg-white p-3"
                      >
                        <Link
                          to={`/products/${product.sku || product._id}`}
                          className={`flex items-center gap-2.5 min-w-0 flex-1 ${focusRing}`}
                        >
                          <span className="w-16 h-16 rounded-xl overflow-hidden bg-emerald-50 flex items-center justify-center shrink-0">
                            {product.thumbnail || product.imageUrl ? (
                              <img
                                src={product.thumbnail || product.imageUrl}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <ImageOff size={16} className="text-emerald-300" aria-hidden="true" />
                            )}
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm font-semibold text-emerald-950 leading-tight line-clamp-2">
                              {product.displayName || product.name}
                            </span>
                            {product.price != null && (
                              <span className="block text-[13px] font-semibold text-emerald-700 mt-1">₹{product.price}</span>
                            )}
                          </span>
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleRemoveFavourite(product._id)}
                          aria-label={`Remove ${product.displayName || product.name} from favourites`}
                          className={`shrink-0 p-1.5 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors ${focusRing}`}
                        >
                          <Trash2 size={15} aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* Profile — only the field the API accepts is editable. */}
              <section
                id="profile"
                aria-label="Profile"
                className="bg-white rounded-[1.5rem] border border-emerald-100 shadow-soft p-6 scroll-mt-28"
              >
                <div className="flex items-center justify-between gap-3 mb-4">
                  <h2 className="flex items-center gap-2.5 text-xl font-bold text-emerald-950">
                    <User size={20} className="text-emerald-700" aria-hidden="true" />
                    Profile
                  </h2>
                  {!editingProfile && (
                    <button
                      type="button"
                      onClick={() => setEditingProfile(true)}
                      className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-emerald-200 text-emerald-800 text-xs font-semibold hover:bg-emerald-50 transition-colors ${focusRing}`}
                    >
                      <Pencil size={13} aria-hidden="true" />
                      Edit
                    </button>
                  )}
                </div>

                <form onSubmit={handleProfileSubmit} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="name" className={labelClass}>Full name</label>
                    {editingProfile ? (
                      <input
                        id="name"
                        value={profileForm.name}
                        onChange={(e) => setProfileForm((p) => ({ ...p, name: e.target.value }))}
                        className={inputClass}
                      />
                    ) : (
                      <p className={`${inputClass} bg-gray-50 flex items-center`}>{customer.name}</p>
                    )}
                  </div>
                  {/* Phone and email are not editable through the customer API,
                      so they stay read-only however the reference draws them. */}
                  <div>
                    <label className={labelClass}>Phone</label>
                    <p className={`${inputClass} bg-gray-50 text-ink-secondary flex items-center`}>{customer.phone}</p>
                  </div>
                  <div>
                    <label className={labelClass}>Email</label>
                    <p className={`${inputClass} bg-gray-50 text-ink-secondary flex items-center truncate`}>{customer.email}</p>
                  </div>

                  {profileMessage && (
                    <p className="sm:col-span-3 text-sm text-emerald-700">{profileMessage}</p>
                  )}
                  {editingProfile && (
                    <div className="sm:col-span-3 flex items-center gap-3">
                      <button type="submit" disabled={profileSaving} className={`${btnPrimary} ${btnMd}`}>
                        {profileSaving ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingProfile(false);
                          setProfileForm({ name: customer.name || '' });
                        }}
                        className={`text-sm font-semibold text-ink-secondary ${focusRing}`}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </form>
              </section>

              {/* Addresses — the existing canonical flow, unchanged. */}
              <section
                id="addresses"
                aria-label="Delivery addresses"
                className="bg-white rounded-[1.5rem] border border-emerald-100 shadow-soft p-6 scroll-mt-28"
              >
                <div className="flex items-center justify-between gap-3 mb-4">
                  <h2 className="flex items-center gap-2.5 text-xl font-bold text-emerald-950">
                    <MapPin size={20} className="text-emerald-700" aria-hidden="true" />
                    Delivery Addresses
                  </h2>
                  <button
                    type="button"
                    onClick={() => setAddressFormOpen((open) => !open)}
                    aria-expanded={addressFormOpen}
                    className={`inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-gradient-primary text-white text-[13px] font-semibold shadow-glow-teal ${focusRing}`}
                  >
                    {addressFormOpen ? 'Close' : '+ Add New Address'}
                  </button>
                </div>

                {addresses === null && <SkeletonBlock className="h-20 rounded-2xl" />}

                {addresses !== null && addresses.length === 0 && !addressFormOpen && (
                  <p className="text-sm text-ink-secondary">
                    No saved addresses yet. Add one to speed up checkout.
                  </p>
                )}

                {addresses !== null && addresses.length > 0 && (
                  <ul className="space-y-3 mb-4">
                    {addresses.map((address) => (
                      <li
                        key={address._id}
                        className={`flex items-start gap-4 rounded-2xl border p-4 sm:p-5 ${
                          address.isDefault ? 'border-emerald-200 bg-emerald-50/60' : 'border-emerald-100'
                        }`}
                      >
                        <span className="w-12 h-12 rounded-xl bg-white border border-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                          <MapPin size={20} aria-hidden="true" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-[15px] font-bold text-emerald-950">
                            {address.label || 'Address'}
                            {address.isDefault && (
                              <span className="ml-2 text-[11px] font-semibold text-emerald-700">(Default)</span>
                            )}
                          </p>
                          <p className="text-[13px] text-ink-secondary leading-relaxed mt-1">
                            {[address.line1, address.line2, address.city, address.state, address.pincode]
                              .filter(Boolean)
                              .join(', ')}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {!address.isDefault && (
                            <button
                              type="button"
                              onClick={() => handleSetDefault(address._id)}
                              aria-label={`Set ${address.label || 'address'} as default`}
                              className={`p-1.5 rounded-full text-gray-400 hover:text-emerald-700 hover:bg-emerald-50 transition-colors ${focusRing}`}
                            >
                              <Star size={16} aria-hidden="true" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleDelete(address._id)}
                            aria-label={`Delete ${address.label || 'address'}`}
                            className={`p-1.5 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors ${focusRing}`}
                          >
                            <Trash2 size={16} aria-hidden="true" />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                {addressFormOpen && (
                  <form onSubmit={handleAddAddress} className="grid grid-cols-1 lg:grid-cols-[1fr_20rem] gap-4 xl:gap-5 border-t border-emerald-50 pt-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      <input placeholder="Label (e.g. Home)" className={inputClass} value={newAddress.label} onChange={(e) => setNewAddress((p) => ({ ...p, label: e.target.value }))} />
                      <input placeholder="Pincode" className={inputClass} value={newAddress.pincode} onChange={(e) => setNewAddress((p) => ({ ...p, pincode: e.target.value }))} />
                      <input placeholder="Address line 1" className={`sm:col-span-2 ${inputClass}`} value={newAddress.line1} onChange={(e) => setNewAddress((p) => ({ ...p, line1: e.target.value }))} />
                      <input placeholder="Address line 2 (optional)" className={`sm:col-span-2 ${inputClass}`} value={newAddress.line2} onChange={(e) => setNewAddress((p) => ({ ...p, line2: e.target.value }))} />
                      <input placeholder="City" className={inputClass} value={newAddress.city} onChange={(e) => setNewAddress((p) => ({ ...p, city: e.target.value }))} />
                      <input placeholder="State" className={inputClass} value={newAddress.state} onChange={(e) => setNewAddress((p) => ({ ...p, state: e.target.value }))} />
                      <input placeholder="Landmark (optional)" className={`sm:col-span-2 ${inputClass}`} value={newAddress.landmark} onChange={(e) => setNewAddress((p) => ({ ...p, landmark: e.target.value }))} />
                    </div>

                    {/* The canonical picker — same component and coordinates
                        contract the page already used. */}
                    <div className="lg:row-span-2">
                      <LocationPickerMap
                        coordinates={newAddress.coordinates}
                        onChange={(coordinates) => setNewAddress((p) => ({ ...p, coordinates }))}
                        onAddressChange={handleMapAddressChange}
                      />
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      <button type="submit" disabled={addingAddress} className={`${btnPrimary} ${btnMd}`}>
                        {addingAddress ? 'Adding...' : 'Save Address'}
                      </button>
                      {/* The existing useCurrentLocation flow — not a second
                          geolocation implementation. */}
                      <button
                        type="button"
                        onClick={handlePinLocation}
                        disabled={locating}
                        className={`inline-flex items-center gap-2 text-sm font-medium px-4 h-11 rounded-full border transition-colors disabled:opacity-60 ${
                          newAddress.coordinates
                            ? 'border-emerald-700 bg-emerald-50 text-emerald-700'
                            : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                        } ${focusRing}`}
                      >
                        <LocateFixed size={16} aria-hidden="true" />
                        {locating ? 'Locating...' : newAddress.coordinates ? 'Location pinned' : 'Use my current location'}
                      </button>
                    </div>

                    {locationError && <p className="lg:col-span-2 text-sm text-red-600">{locationError}</p>}
                    {addressError && <p className="lg:col-span-2 text-sm text-red-600">{addressError}</p>}
                  </form>
                )}
              </section>
            </div>

            {/* ── Right column ──────────────────────────────────────────── */}
            <div className="min-w-0 space-y-5 xl:mt-[7rem]">
              <ul className="space-y-3">
                {QUICK_ACTIONS.map(({ id, to, icon: Icon, title, description }) => (
                  <li key={id}>
                    <Link
                      to={to}
                      className={`w-full flex items-center gap-3.5 bg-white rounded-[1.25rem] border border-emerald-100 shadow-soft hover:shadow-card transition-shadow p-4 ${focusRing}`}
                    >
                      <span className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                        <Icon size={22} aria-hidden="true" />
                      </span>
                      <span className="min-w-0 flex-1 text-left">
                        <span className="block text-[15px] font-bold text-emerald-950 leading-tight">{title}</span>
                        <span className="block text-[13px] text-ink-secondary leading-tight mt-1">{description}</span>
                      </span>
                      <ChevronRight size={18} className="text-emerald-300 shrink-0" aria-hidden="true" />
                    </Link>
                  </li>
                ))}
              </ul>

              {/* Recent transactions — the wallet ledger's own entries. */}
              <section
                aria-label="Recent transactions"
                className="bg-white rounded-[1.5rem] border border-emerald-100 shadow-soft p-6"
              >
                <div className="flex items-center justify-between gap-3 mb-3">
                  <h2 className="flex items-center gap-2.5 text-lg font-bold text-emerald-950">
                    <Receipt size={19} className="text-accent-500" aria-hidden="true" />
                    Recent Transactions
                  </h2>
                </div>

                {wallet.status === 'loading' && <SkeletonBlock className="h-24 rounded-xl" />}
                {wallet.status === 'error' && (
                  <p className="text-sm text-ink-secondary">Transactions are unavailable right now.</p>
                )}
                {wallet.status === 'success' && transactions.length === 0 && (
                  <p className="text-sm text-ink-secondary">No wallet activity yet.</p>
                )}
                {wallet.status === 'success' && transactions.length > 0 && (
                  <ul className="divide-y divide-emerald-50">
                    {transactions.map((txn) => {
                      // Direction comes from the ledger's own `type`, never
                      // from the label text.
                      const credit = txn.type === 'credit';
                      return (
                        <li key={txn.id} className="flex items-center gap-3 py-3.5 first:pt-0 last:pb-0">
                          <span
                            className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                              credit ? 'bg-success-50 text-success-700' : 'bg-accent-50 text-accent-600'
                            }`}
                          >
                            {credit ? (
                              <ArrowDownLeft size={16} aria-hidden="true" />
                            ) : (
                              <ArrowUpRight size={16} aria-hidden="true" />
                            )}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-semibold text-emerald-950 leading-tight truncate">
                              {txn.reasonLabel || txn.reason}
                            </span>
                            <span className="block text-xs text-ink-secondary leading-tight mt-1">
                              {formatTxnDate(txn.createdAt)}
                            </span>
                          </span>
                          <span
                            className={`text-sm font-bold shrink-0 ${
                              credit ? 'text-success-700' : 'text-accent-600'
                            }`}
                          >
                            {credit ? '+' : '−'}₹{txn.amount}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
};

export default ProfilePage;
